/**
 * Highlights recorder — captures compact clips around key moments during a match,
 * together with what the director actually had ON AIR, so the end-of-match reel
 * replays the player's own broadcast (good cuts, bad cuts and all).
 *
 *   const rec = createHighlightsRecorder(config, kit);
 *   rec.record(snapshot, newEvents, director);   // every sim step (internally 15 Hz)
 *   rec.feedback(gradeFeedback);                 // every grader toast
 *   const data = rec.finish(cuts, report);       // at full time
 *
 * Memory: a 30 s rolling ring of compact frames (≈0.8 KB each at 15 Hz) plus at most
 * MAX_CLIPS finished clips that share frame objects with the ring (no copies).
 * When over budget the least important clip is dropped (goals always win).
 *
 * Pure data + no DOM / Three.js, so it is safe to run in the session's sim step.
 */
import type {
  CameraDef, CutRecord, DirectorState, GradeFeedback, GradeReport, MatchConfig, MatchEvent, MatchEventType,
  MatchPhase, PlayerAnim, TeamSide, WorldSnapshot,
} from '../core/contracts';

export const CLIP_PRE_S = 7;
export const CLIP_POST_S = 4;
export const RECORD_HZ = 15;
export const MAX_CLIPS = 12;
const KEY_IMPORTANCE = 0.6;
const MAX_CLIP_S = 22;
const RING_S = 30;
const SKIP_TYPES: ReadonlySet<MatchEventType> = new Set<MatchEventType>(['kickoff', 'half_time', 'full_time', 'substitution', 'celebration', 'crowd_moment', 'manager_reaction']);

const ANIMS: readonly PlayerAnim[] = ['idle', 'jog', 'run', 'sprint', 'kick', 'header', 'tackle', 'dive', 'fall', 'celebrate', 'dejected', 'throw'];
const PHASES: readonly MatchPhase[] = ['pre_match', 'kickoff', 'open_play', 'stoppage', 'set_piece', 'goal_celebration', 'half_time', 'full_time'];

/** Floats per player: pos3 vel3 heading animPhase. */
const PF = 8;
/** Header floats: ball pos3 vel3 spin, focus3, intensity, officials 9. */
const HF = 7 + 3 + 1 + 9;

export interface RosterEntry { id: string; team: TeamSide; number: number }

/** One compact recorded frame (immutable once recorded; shared between ring and clips). */
export interface ClipFrame {
  t: number;
  matchClock: number;
  half: 1 | 2;
  phase: number;             // index into PHASES
  score: [number, number];
  possession: TeamSide | null;
  ballOwner: string | null;
  roster: RosterEntry[];     // shared reference until a substitution changes it
  anim: Uint8Array;          // index into ANIMS
  flags: Uint8Array;         // bit0 hasBall, bit1 sentOff
  data: Float32Array;
  /** What was on PGM: camera id and the replay playhead (NaN = live). */
  airCam: string;
  airHead: number;
  replayEventId: number | null;
}

export interface ClipVerdict { label: string; severity: GradeFeedback['severity'] }

export interface HighlightClip {
  id: number;
  /** Playback window (sim seconds). Frames may start earlier to cover on-air replays. */
  fromT: number;
  toT: number;
  frames: ClipFrame[];
  /** Key events in the window, chronological. `primary` is the headline one. */
  events: MatchEvent[];
  primary: MatchEvent;
  /** Program cut timeline for the window. First entry is the camera on air at fromT. */
  cuts: CutRecord[];
  /** Grade verdict for the primary event ("ON THE GOAL!", "MISSED IT!"). */
  verdict: ClipVerdict | null;
  /** Every feedback toast tied to events in this clip. */
  feedback: GradeFeedback[];
  score: number;
}

export interface HighlightsData {
  config: MatchConfig;
  kit: CameraDef[];
  clips: HighlightClip[];
}

export interface HighlightsRecorder {
  record(s: WorldSnapshot, events: readonly MatchEvent[], director: DirectorState): void;
  feedback(f: GradeFeedback): void;
  /** Close any open clip and return the reel data. Safe to call more than once. */
  finish(cuts: readonly CutRecord[], report?: GradeReport | null): HighlightsData;
  readonly clipCount: number;
}

// ---------------------------------------------------------------------------
// Frame encode / decode
// ---------------------------------------------------------------------------

function encode(s: WorldSnapshot, roster: RosterEntry[], d: DirectorState): ClipFrame {
  const n = s.players.length;
  const data = new Float32Array(HF + n * PF);
  const anim = new Uint8Array(n);
  const flags = new Uint8Array(n);
  const b = s.ball, o = s.officials;
  data[0] = b.pos.x; data[1] = b.pos.y; data[2] = b.pos.z;
  data[3] = b.vel.x; data[4] = b.vel.y; data[5] = b.vel.z; data[6] = b.spin;
  data[7] = s.focus.x; data[8] = s.focus.y; data[9] = s.focus.z; data[10] = s.intensity;
  data[11] = o.referee.x; data[12] = o.referee.y; data[13] = o.referee.z;
  data[14] = o.assistant1.x; data[15] = o.assistant1.y; data[16] = o.assistant1.z;
  data[17] = o.assistant2.x; data[18] = o.assistant2.y; data[19] = o.assistant2.z;
  for (let i = 0; i < n; i++) {
    const p = s.players[i]!, k = HF + i * PF;
    data[k] = p.pos.x; data[k + 1] = p.pos.y; data[k + 2] = p.pos.z;
    data[k + 3] = p.vel.x; data[k + 4] = p.vel.y; data[k + 5] = p.vel.z;
    data[k + 6] = p.heading; data[k + 7] = p.animPhase;
    anim[i] = Math.max(0, ANIMS.indexOf(p.anim));
    flags[i] = (p.hasBall ? 1 : 0) | (p.sentOff ? 2 : 0);
  }
  const r = d.replay;
  return {
    t: s.t, matchClock: s.matchClock, half: s.half, phase: Math.max(0, PHASES.indexOf(s.phase)),
    score: [s.score.home, s.score.away], possession: s.possession, ballOwner: b.ownerId,
    roster, anim, flags, data,
    airCam: r.active ? r.camId : d.programCamId,
    airHead: r.active ? r.headT : NaN,
    replayEventId: r.active ? r.eventId : null,
  };
}

/** Expand a compact frame into a preallocated WorldSnapshot (see createEmptySnapshot). */
export function decodeFrame(f: ClipFrame, out: WorldSnapshot): WorldSnapshot {
  const d = f.data;
  out.t = f.t; out.matchClock = f.matchClock; out.half = f.half; out.phase = PHASES[f.phase] ?? 'open_play';
  out.score.home = f.score[0]; out.score.away = f.score[1]; out.possession = f.possession;
  const b = out.ball;
  b.pos.x = d[0]!; b.pos.y = d[1]!; b.pos.z = d[2]!; b.vel.x = d[3]!; b.vel.y = d[4]!; b.vel.z = d[5]!; b.spin = d[6]!;
  b.ownerId = f.ballOwner;
  out.focus.x = d[7]!; out.focus.y = d[8]!; out.focus.z = d[9]!; out.intensity = d[10]!;
  const o = out.officials;
  o.referee.x = d[11]!; o.referee.y = d[12]!; o.referee.z = d[13]!;
  o.assistant1.x = d[14]!; o.assistant1.y = d[15]!; o.assistant1.z = d[16]!;
  o.assistant2.x = d[17]!; o.assistant2.y = d[18]!; o.assistant2.z = d[19]!;
  const n = f.roster.length;
  while (out.players.length < n) {
    out.players.push({ id: '', team: 'home', number: 0, pos: { x: 0, y: 0, z: 0 }, vel: { x: 0, y: 0, z: 0 }, heading: 0, anim: 'idle', animPhase: 0, hasBall: false });
  }
  if (out.players.length > n) out.players.length = n;
  for (let i = 0; i < n; i++) {
    const p = out.players[i]!, r = f.roster[i]!, k = HF + i * PF;
    p.id = r.id; p.team = r.team; p.number = r.number;
    p.pos.x = d[k]!; p.pos.y = d[k + 1]!; p.pos.z = d[k + 2]!;
    p.vel.x = d[k + 3]!; p.vel.y = d[k + 4]!; p.vel.z = d[k + 5]!;
    p.heading = d[k + 6]!; p.animPhase = d[k + 7]!;
    p.anim = ANIMS[f.anim[i]!] ?? 'idle';
    p.hasBall = (f.flags[i]! & 1) !== 0;
    const off = (f.flags[i]! & 2) !== 0;
    if (off || p.sentOff) p.sentOff = off;
  }
  return out;
}

/** Index of the last frame with t <= time (clamped to [0, len-2] so i+1 exists when len ≥ 2). */
export function frameIndexAt(frames: readonly ClipFrame[], time: number): number {
  let lo = 0, hi = frames.length - 1;
  if (hi <= 0) return 0;
  if (time <= frames[0]!.t) return 0;
  if (time >= frames[hi]!.t) return hi - 1;
  while (hi - lo > 1) {
    const mid = (lo + hi) >> 1;
    if (frames[mid]!.t <= time) lo = mid; else hi = mid;
  }
  return lo;
}

// ---------------------------------------------------------------------------
// Recorder
// ---------------------------------------------------------------------------

interface Pending { fromT: number; toT: number; events: MatchEvent[] }

const clipScore = (events: readonly MatchEvent[]): number =>
  events.reduce((m, e) => Math.max(m, e.importance + (e.type === 'goal' ? 1 : e.type === 'red_card' || e.type === 'penalty' ? 0.3 : 0)), 0);

const primaryOf = (events: readonly MatchEvent[]): MatchEvent =>
  events.reduce((a, b) => (clipScore([b]) > clipScore([a]) ? b : a));

export function createHighlightsRecorder(config: MatchConfig, kit: readonly CameraDef[]): HighlightsRecorder {
  const ring: ClipFrame[] = [];
  const minDt = (1 / RECORD_HZ) * 0.98;
  let roster: RosterEntry[] = [];
  let pending: Pending | null = null;
  const clips: HighlightClip[] = [];
  const keyIds = new Set<number>();
  const fbByEvent = new Map<number, GradeFeedback[]>();
  let nextId = 1;
  let lastCuts: readonly CutRecord[] = [];

  const rosterFor = (s: WorldSnapshot): RosterEntry[] => {
    const ps = s.players;
    let same = roster.length === ps.length;
    for (let i = 0; same && i < ps.length; i++) if (roster[i]!.id !== ps[i]!.id) same = false;
    if (!same) roster = ps.map((p) => ({ id: p.id, team: p.team, number: p.number }));
    return roster;
  };

  const close = (p: Pending, cuts: readonly CutRecord[]): void => {
    if (!ring.length) return;
    // Frames for the window, extended back to cover any replay that was on air during it.
    let need = p.fromT;
    for (const f of ring) if (f.t >= p.fromT && f.t <= p.toT && Number.isFinite(f.airHead)) need = Math.min(need, f.airHead - 0.3);
    need = Math.max(need, p.fromT - (RING_S - MAX_CLIP_S));
    const frames = ring.filter((f) => f.t >= need - 1e-6 && f.t <= p.toT + 1e-6);
    if (frames.length < 4) return;
    const fromT = Math.max(p.fromT, frames[0]!.t);
    const toT = Math.min(p.toT, frames[frames.length - 1]!.t);
    // Program timeline: what was on air at fromT, then every cut inside the window.
    const startFrame = frames[frameIndexAt(frames, fromT)]!;
    const clipCuts: CutRecord[] = [{ t: fromT, camId: startFrame.airCam, transition: 'cut', isReplay: Number.isFinite(startFrame.airHead) }];
    for (const c of cuts) if (c.t > fromT && c.t <= toT) clipCuts.push({ ...c });
    const events = p.events.slice().sort((a, b) => a.t - b.t);
    clips.push({
      id: nextId++, fromT, toT, frames, events, primary: primaryOf(events), cuts: clipCuts,
      verdict: null, feedback: [], score: clipScore(events),
    });
    // Bounded: drop the least important (earliest on ties).
    while (clips.length > MAX_CLIPS) {
      let worst = 0;
      for (let i = 1; i < clips.length; i++) if (clips[i]!.score < clips[worst]!.score) worst = i;
      clips.splice(worst, 1);
    }
  };

  const verdictFor = (clip: HighlightClip, report?: GradeReport | null): ClipVerdict | null => {
    const fb = clip.feedback;
    const cov = fb.find((f) => f.eventId === clip.primary.id && f.category === 'coverage');
    if (cov) return { label: cov.label, severity: cov.severity };
    const km = report?.keyMoments.find((k) => k.eventId === clip.primary.id);
    if (km) return km.covered ? { label: clip.primary.type === 'goal' ? 'ON THE GOAL!' : 'CAUGHT IT', severity: 'great' } : { label: 'MISSED IT!', severity: 'terrible' };
    const any = fb.find((f) => f.eventId === clip.primary.id);
    return any ? { label: any.label, severity: any.severity } : null;
  };

  return {
    get clipCount() { return clips.length + (pending ? 1 : 0); },

    record(s, events, director) {
      lastCuts = director.cuts;
      const last = ring[ring.length - 1];
      if (last && s.t < last.t) { ring.length = 0; pending = null; }
      if (!last || s.t - last.t >= minDt) {
        ring.push(encode(s, rosterFor(s), director));
        const cutoff = s.t - RING_S;
        let drop = 0;
        while (drop < ring.length && ring[drop]!.t < cutoff) drop++;
        if (drop) ring.splice(0, drop);
      }
      for (const e of events) {
        if (e.importance < KEY_IMPORTANCE || SKIP_TYPES.has(e.type)) continue;
        keyIds.add(e.id);
        if (pending && e.t - CLIP_PRE_S <= pending.toT && e.t + CLIP_POST_S - pending.fromT <= MAX_CLIP_S) {
          pending.toT = Math.max(pending.toT, e.t + CLIP_POST_S);
          pending.events.push(e);
        } else {
          if (pending) close(pending, director.cuts);
          pending = { fromT: e.t - CLIP_PRE_S, toT: e.t + CLIP_POST_S, events: [e] };
        }
      }
      if (pending && s.t >= pending.toT) { close(pending, director.cuts); pending = null; }
    },

    feedback(f) {
      if (f.eventId === undefined || !keyIds.has(f.eventId)) return;
      let arr = fbByEvent.get(f.eventId);
      if (!arr) { arr = []; fbByEvent.set(f.eventId, arr); }
      if (arr.length < 12) arr.push({ ...f });
    },

    finish(cuts, report) {
      if (pending) { close(pending, cuts.length ? cuts : lastCuts); pending = null; }
      for (const c of clips) {
        c.feedback = c.events.flatMap((e) => fbByEvent.get(e.id) ?? []);
        c.verdict = verdictFor(c, report);
      }
      return { config, kit: kit.slice(), clips: clips.slice().sort((a, b) => a.fromT - b.fromT) };
    },
  };
}
